import { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Header from '../Header';
import Sidebar from '../Sidebar';
import watermarkImage from '../assets/coverzone-watermark.png';
import { usePermissions } from '../context/PermissionsContext';

const API_URL = process.env.REACT_APP_API_URL;

interface Employee {
  id: number;
  name: string;
  surname: string;
  id_passport_number: string;
  contact_number: string;
  email: string;
  active: boolean; // Corresponds to 'Status'
  re_compliant: boolean;
  employment_start_date: string;
  employee_code: string;
  work_model: string;
  position: string;
  roles: string;
}

// Interface for a single note
interface EmployeeNote {
  id: number;
  note_content: string;
  created_at: string; // Date string from DB
}

// NEW Interface for a single document
interface EmployeeDocument {
  id: number;
  file_name: string;
  file_path: string; // The path on the server (we won't directly use this for display on frontend)
  file_size: number;
  file_type: string;
  uploaded_at: string; // Date string from DB
}

// NEW Interface for a single qualification (assuming file-based)
interface EmployeeQualification {
  id: number;
  file_name: string;
  file_path: string;
  file_size: number;
  file_type: string;
  uploaded_at: string;
}

const EmployeeDetail = () => {
  const { can } = usePermissions();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const employeeNameFromState = location.state?.employeeName || 'Employee Details';

  const [employee, setEmployee] = useState<Employee | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [user, setUser] = useState<{ id: number; email: string; username?: string; organization?: string; surname?: string; client_number?: string } | null>(null);
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [activeTab, setActiveTab] = useState('Profile');

  // STATE FOR NOTES
  const [newNoteContent, setNewNoteContent] = useState<string>('');
  const [employeeNotes, setEmployeeNotes] = useState<EmployeeNote[]>([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [notesError, setNotesError] = useState<string | null>(null);

  // Control visibility of the new note input form
  const [showNewNoteForm, setShowNewNoteForm] = useState(false);

  // STATE FOR DOCUMENTS
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [employeeDocuments, setEmployeeDocuments] = useState<EmployeeDocument[]>([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [documentsError, setDocumentsError] = useState<string | null>(null);

  // STATE FOR QUALIFICATIONS
  const [selectedQualificationFile, setSelectedQualificationFile] = useState<File | null>(null);
  const [employeeQualifications, setEmployeeQualifications] = useState<EmployeeQualification[]>([]);
  const [qualificationsLoading, setQualificationsLoading] = useState(false);
  const [qualificationsError, setQualificationsError] = useState<string | null>(null);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editFormData, setEditFormData] = useState<Partial<Employee>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUser(response.data);
      } catch (error) {
        console.error('Error fetching user:', error);
        localStorage.removeItem('token');
        navigate('/login');
      }
    };

    const fetchEmployeeDetails = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/employees/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setEmployee(response.data);
        setError(null);
      } catch (err) {
        setError('Failed to load employee details');
        console.error('Employee detail error:', err);
        setEmployee(null);
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
    if (id) {
      fetchEmployeeDetails();
    } else {
      setError('Employee ID not provided.');
      setLoading(false);
    }
  }, [id, navigate]);

  // useEffect for fetching notes when the tab changes or employee ID is available
  useEffect(() => {
    const fetchNotes = async () => {
      if (activeTab === 'Notes' && id) { // Only fetch if 'Notes' tab is active and employee ID exists
        setNotesLoading(true);
        setNotesError(null);
        try {
          const token = localStorage.getItem('token');
          const response = await axios.get(`${API_URL}/employees/${id}/notes`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setEmployeeNotes(response.data);
        } catch (err) {
          console.error('Error fetching employee notes:', err);
          setNotesError('Failed to load notes.');
        } finally {
          setNotesLoading(false);
        }
      }
    };
    fetchNotes();
    // When switching away from notes tab, hide the new note form
    if (activeTab !== 'Notes') {
        setShowNewNoteForm(false);
        setNewNoteContent(''); // Also clear content if navigating away
    }
  }, [activeTab, id]);

  // useEffect for fetching documents when the tab changes or employee ID is available
  useEffect(() => {
    const fetchDocuments = async () => {
      if (activeTab === 'Documents' && id) {
        setDocumentsLoading(true);
        setDocumentsError(null);
        try {
          const token = localStorage.getItem('token');
          const response = await axios.get(`${API_URL}/employees/${id}/documents`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setEmployeeDocuments(response.data);
        } catch (err) {
          console.error('Error fetching employee documents:', err);
          setDocumentsError('Failed to load documents.');
        } finally {
          setDocumentsLoading(false);
        }
      }
    };
    fetchDocuments();
    // Clear selected file and reset error when switching tabs
    if (activeTab !== 'Documents') {
      setSelectedFile(null);
      setDocumentsError(null);
    }
  }, [activeTab, id]);

  // useEffect for fetching qualifications when the tab changes or employee ID is available
  useEffect(() => {
    const fetchQualifications = async () => {
      if (activeTab === 'Qualifications' && id) {
        setQualificationsLoading(true);
        setQualificationsError(null);
        try {
          const token = localStorage.getItem('token');
          const response = await axios.get(`${API_URL}/employees/${id}/qualifications`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setEmployeeQualifications(response.data);
        } catch (err) {
          console.error('Error fetching employee qualifications:', err);
          setQualificationsError('Failed to load qualifications.');
        } finally {
          setQualificationsLoading(false);
        }
      }
    };
    fetchQualifications();
    // Clear selected file and reset error when switching tabs
    if (activeTab !== 'Qualifications') {
      setSelectedQualificationFile(null);
      setQualificationsError(null);
    }
  }, [activeTab, id]);

  // Handle file selection
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setSelectedFile(event.target.files[0]);
      setDocumentsError(null); // Clear any previous errors
    } else {
      setSelectedFile(null);
    }
  };

  // Handle document upload
  const handleDocumentUpload = async () => {
    if (!selectedFile) {
      setDocumentsError('Please select a file to upload.');
      return;
    }
    if (!id) {
      setDocumentsError('Cannot upload document: Employee ID is missing.');
      return;
    }

    setDocumentsLoading(true);
    setDocumentsError(null);

    const formData = new FormData();
    formData.append('document', selectedFile); // 'document' matches the name in multer's upload.single('document')

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/employees/${id}/documents`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data', // Important for file uploads
          },
        }
      );
      // Add the newly uploaded document metadata to the list
      setEmployeeDocuments([response.data, ...employeeDocuments]);
      setSelectedFile(null); // Clear the selected file
      // Optionally, reset the file input field
      const fileInput = document.getElementById('documentUploadInput') as HTMLInputElement;
      if (fileInput) fileInput.value = '';

      alert('Document uploaded successfully!'); // User feedback
    } catch (err: any) {
      console.error('Error uploading document:', err);
      // Check for backend error message
      if (err.response && err.response.data && err.response.data.message) {
        setDocumentsError(`Upload failed: ${err.response.data.message}`);
      } else if (err.message) {
        setDocumentsError(`Upload failed: ${err.message}`); // Multer error or network error
      } else {
        setDocumentsError('Failed to upload document.');
      }
    } finally {
      setDocumentsLoading(false);
    }
  };

  // Handle file selection for Qualifications
  const handleQualificationFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setSelectedQualificationFile(event.target.files[0]);
      setQualificationsError(null); // Clear any previous errors
    } else {
      setSelectedQualificationFile(null);
    }
  };

  // Handle qualification upload
  const handleQualificationUpload = async () => {
    if (!selectedQualificationFile) {
      setQualificationsError('Please select a qualification file to upload.');
      return;
    }
    if (!id) {
      setQualificationsError('Cannot upload qualification: Employee ID is missing.');
      return;
    }

    setQualificationsLoading(true);
    setQualificationsError(null);

    const formData = new FormData();
    formData.append('qualification', selectedQualificationFile); // 'qualification' will be the field name on the backend

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/employees/${id}/qualifications`, // NEW API endpoint
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      setEmployeeQualifications([response.data, ...employeeQualifications]);
      setSelectedQualificationFile(null); // Clear the selected file
      const fileInput = document.getElementById('qualificationUploadInput') as HTMLInputElement;
      if (fileInput) fileInput.value = '';

      alert('Qualification uploaded successfully!');
    } catch (err: any) {
      console.error('Error uploading qualification:', err);
      if (err.response && err.response.data && err.response.data.message) {
        setQualificationsError(`Upload failed: ${err.response.data.message}`);
      } else if (err.message) {
        setQualificationsError(`Upload failed: ${err.message}`);
      } else {
        setQualificationsError('Failed to upload qualification.');
      }
    } finally {
      setQualificationsLoading(false);
    }
  };

  // Helper to format file size
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const toggleSidebar = () => {
    setIsSidebarVisible(!isSidebarVisible);
  };

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    console.error('Failed to load watermark image:', e);
  };

  const handleImageLoad = (type: string) => {
    console.log(`${type} image loaded successfully`);
  };

  // Handle creating a new note (shows the textarea)
  const handleCreateNewNote = () => {
    setNewNoteContent(''); // Clear the textarea for a new note
    setShowNewNoteForm(true); // Show the new note form
  };

  // Handle canceling the new note
  const handleCancelNewNote = () => {
    setNewNoteContent('');
    setShowNewNoteForm(false);
  };

  const handleEditEmployee = () => {
    if (employee) {
      setEditFormData({
        name: employee.name,
        surname: employee.surname,
        id_passport_number: employee.id_passport_number,
        contact_number: employee.contact_number,
        email: employee.email,
        active: employee.active,
        re_compliant: employee.re_compliant,
        employment_start_date: employee.employment_start_date,
        employee_code: employee.employee_code,
        work_model: employee.work_model,
        position: employee.position,
        roles: employee.roles,
      });
      setIsEditModalOpen(true);
      setSaveError(null);
    }
  };

  const handleSaveEdit = async () => {
    setIsSaving(true);
    setSaveError(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_URL}/employees/${id}`,
        editFormData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setEmployee(response.data);
      setIsEditModalOpen(false);
      alert('Employee updated successfully!');
    } catch (err: any) {
      console.error('Error updating employee:', err);
      setSaveError(err.response?.data?.message || 'Failed to update employee');
    } finally {
      setIsSaving(false);
    }
  };

  //Handle saving the current note
  const handleSaveNote = async () => {
    if (!newNoteContent.trim()) {
      alert('Note content cannot be empty.');
      return;
    }
    if (!id) {
      alert('Cannot save note: Employee ID is missing.');
      return;
    }

    setNotesLoading(true);
    setNotesError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/employees/${id}/notes`,
        { note_content: newNoteContent },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      // Add the newly saved note to the top of the existing notes list
      setEmployeeNotes([response.data, ...employeeNotes]);
      setNewNoteContent(''); // Clear the textarea after saving
      setShowNewNoteForm(false); // Hide the form after successful save
    } catch (err) {
      console.error('Error saving note:', err);
      setNotesError('Failed to save note.');
    } finally {
      setNotesLoading(false);
    }
  };

  if (!user) return <div>Loading user information...</div>;

  return (
    <div className="min-h-screen bg-gray-100 flex relative">
      <div className="fixed inset-0 flex items-center justify-center pointer-events-none z-0">
        <img
          src={watermarkImage}
          alt="Watermark"
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-100 h-100 opacity-5 z-0 select-none"
          onError={handleImageError}
          onLoad={() => handleImageLoad('Watermark')}
        />
      </div>

      <Sidebar isVisible={isSidebarVisible} toggleSidebar={toggleSidebar} />

      <div className={`flex-1 flex flex-col transition-all duration-300 ${isSidebarVisible ? 'ml-64' : 'ml-0'}`}>
        <Header
          title={employeeNameFromState}
          user={user}
          toggleSidebar={toggleSidebar}
          isSidebarVisible={isSidebarVisible}
          showClientNumber={false}
        />

                <div className="flex-1 p-6">
                  <div className="bg-white rounded-lg shadow-md p-6 max-w-4xl mx-auto w-full">
                    <div className="flex justify-between items-center mb-6">
  <h2 className="text-2xl font-semibold text-gray-800">Employee Details</h2>
  <div className="flex gap-3">
    <button
      onClick={() => navigate('/employees')}
      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
    >
      ← Back
    </button>
    {can('employees', 'write') && (
      <button
        onClick={handleEditEmployee}
        className="px-4 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2"
      >
        Edit Employee
      </button>
    )}
  </div>
</div>

            {/* Tabs Navigation */}
            <div className="border-b border-gray-200 mb-6">
              <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                {['Profile', 'Employment Info', 'Notes', 'Documents', 'Qualifications'].map((tabName) => (
                  <button
                    key={tabName}
                    onClick={() => setActiveTab(tabName)}
                    className={`
                      whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm
                      ${activeTab === tabName
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }
                      focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                    `}
                  >
                    {tabName}
                  </button>
                ))}
              </nav>
            </div>

            {/* Tab Content */}
            {loading && <p className="text-center text-gray-500">Loading employee details...</p>}
            {error && <p className="text-center text-red-500">{error}</p>}
            {employee && !loading && (
              <div>
                {activeTab === 'Profile' && (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
    {/* Name */}
    <div className="flex flex-col">
      <p className="text-gray-500 text-sm font-medium">Name</p>
      <p className="text-gray-900 text-base">{employee.name}</p>
    </div>

    {/* Surname - NOW USING ACTUAL FIELD */}
    <div className="flex flex-col">
      <p className="text-gray-500 text-sm font-medium">Surname</p>
      <p className="text-gray-900 text-base">{employee.surname}</p>
    </div>

    {/* Email */}
    <div className="flex flex-col">
      <p className="text-gray-500 text-sm font-medium">Email</p>
      <p className="text-gray-900 text-base">{employee.email}</p>
    </div>

    {/* ID/Passport Number */}
    <div className="flex flex-col">
      <p className="text-gray-500 text-sm font-medium">ID/Passport Number</p>
      <p className="text-gray-900 text-base">{employee.id_passport_number}</p>
    </div>

    {/* Contact Number */}
    <div className="flex flex-col">
      <p className="text-gray-500 text-sm font-medium">Contact Number</p>
      <p className="text-gray-900 text-base">{employee.contact_number}</p>
    </div>

    {/* Status */}
    <div className="flex flex-col">
      <p className="text-gray-500 text-sm font-medium">Status</p>
      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
        employee.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
      }`}>
        {employee.active ? 'Active' : 'Inactive'}
      </span>
    </div>

    {/* RE Compliant */}
    <div className="flex flex-col">
      <p className="text-gray-500 text-sm font-medium">RE Compliant</p>
      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
        employee.re_compliant ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
      }`}>
        {employee.re_compliant ? 'Compliant' : 'Non-Compliant'}
      </span>
    </div>
  </div>
)}

                {activeTab === 'Employment Info' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"> {/* Using a similar grid layout */}
                    {/* Employment Start Date */}
                    <div className="flex flex-col">
                      <p className="text-gray-500 text-sm font-medium">Employment Start Date</p>
                      <p className="text-gray-900 text-base">
                        {employee.employment_start_date ? new Date(employee.employment_start_date).toLocaleDateString() : 'N/A'}
                      </p>
                    </div>

                    {/* Code */}
                    <div className="flex flex-col">
                      <p className="text-gray-500 text-sm font-medium">Code</p>
                      <p className="text-gray-900 text-base">{employee.employee_code || 'N/A'}</p>
                    </div>

                    {/* Work Model */}
                    <div className="flex flex-col">
                      <p className="text-gray-500 text-sm font-medium">Work Model</p>
                      <p className="text-gray-900 text-base">{employee.work_model || 'N/A'}</p>
                    </div>

                    {/* Position */}
                    <div className="flex flex-col">
                      <p className="text-gray-500 text-sm font-medium">Position</p>
                      <p className="text-gray-900 text-base">{employee.position || 'N/A'}</p>
                    </div>

                    {/* Roles */}
                    <div className="flex flex-col">
                      <p className="text-gray-500 text-sm font-medium">Roles</p>
                      <p className="text-gray-900 text-base">{employee.roles || 'N/A'}</p>
                    </div>

                    {/* Add more employment-specific details here as needed */}
                  </div>
                )}

                {activeTab === 'Notes' && (
                  <div>
                    <h3 className="text-xl font-semibold mb-4">Employee Notes</h3>

                    {/* Button to show the new note form */}
                    {!showNewNoteForm && can('employees', 'write') && (
                      <button
                        onClick={handleCreateNewNote}
                        className="mb-6 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                      >
                        Create New Note
                      </button>
                    )}

                    {/* NEW NOTE FORM - Conditional Rendering */}
                    {showNewNoteForm && (
                      <div className="mb-6 p-4 border border-gray-200 rounded-md bg-gray-50">
                        <label htmlFor="newNote" className="block text-sm font-medium text-gray-700 mb-2">
                          New Note:
                        </label>
                        <textarea
                          id="newNote"
                          className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                          rows={6}
                          placeholder="Type your note here..."
                          value={newNoteContent}
                          onChange={(e) => setNewNoteContent(e.target.value)}
                        ></textarea>
                        <div className="mt-4 flex space-x-3 justify-end"> {/* Align buttons to the right */}
                          <button
                            onClick={handleCancelNewNote}
                            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleSaveNote}
                            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                            disabled={notesLoading}
                          >
                            {notesLoading ? 'Saving...' : 'Save Note'}
                          </button>
                        </div>
                        {notesError && <p className="text-red-500 text-sm mt-2">{notesError}</p>}
                      </div>
                    )}

                    <h4 className="text-lg font-semibold text-gray-800 mb-3">All Notes:</h4>
                    {notesLoading && !showNewNoteForm && <p className="text-gray-500">Loading existing notes...</p>}
                    {employeeNotes.length === 0 && !notesLoading && !notesError && (
                      <p className="text-gray-600">No notes found for this employee.</p>
                    )}
                    {employeeNotes.length > 0 && (
                      <div className="space-y-4">
                        {employeeNotes.map((note) => (
                          <div key={note.id} className="bg-gray-50 p-4 rounded-md shadow-sm border border-gray-200">
                            <p className="text-gray-800 text-sm mb-2 whitespace-pre-wrap">{note.note_content}</p>
                            <p className="text-gray-500 text-xs text-right">
                              {new Date(note.created_at).toLocaleString()}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'Documents' && (
                  <div>
                    <h3 className="text-xl font-semibold mb-4">Upload New Document</h3>

                    <div className="mb-6 p-4 border border-gray-200 rounded-md bg-gray-50">
                      <label htmlFor="documentUploadInput" className="block text-sm font-medium text-gray-700 mb-2">
                        Select Document:
                      </label>
                      <input
                        type="file"
                        id="documentUploadInput"
                        onChange={handleFileChange}
                        className="block w-full text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-white focus:outline-none file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                      />
                      {selectedFile && (
                        <p className="mt-2 text-sm text-gray-600">Selected file: <span className="font-medium">{selectedFile.name}</span> ({formatFileSize(selectedFile.size)})</p>
                      )}
                      {can('employees', 'write') && (
                        <button
                          onClick={handleDocumentUpload}
                          className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                          disabled={documentsLoading || !selectedFile}
                        >
                          {documentsLoading ? 'Uploading...' : 'Upload Document'}
                        </button>
                      )}
                      {documentsError && <p className="text-red-500 text-sm mt-2">{documentsError}</p>}
                    </div>

                    <h4 className="text-lg font-semibold text-gray-800 mb-3">Existing Documents:</h4>
                    {documentsLoading && <p className="text-gray-500">Loading documents...</p>}
                    {employeeDocuments.length === 0 && !documentsLoading && !documentsError && (
                      <p className="text-gray-600">No documents found for this employee.</p>
                    )}
                    {employeeDocuments.length > 0 && (
                      <div className="space-y-4">
                        {employeeDocuments.map((doc) => {
                          const filePathToLog = doc.file_path; // Assign to a variable
                          console.log("Frontend - Document file_path received:", filePathToLog); // Log the variable

                          return (
                            <div key={doc.id} className="bg-gray-50 p-4 rounded-md shadow-sm border border-gray-200 flex justify-between items-center">
                              <div>
                                <p className="text-gray-800 text-sm font-medium">{doc.file_name}</p>
                                <p className="text-gray-500 text-xs">
                                  {formatFileSize(doc.file_size)} &bull; {new Date(doc.uploaded_at).toLocaleDateString()}
                                </p>
                              </div>
                              <a
                                href={`${API_URL}/uploads/${doc.file_path}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="px-3 py-1 bg-green-500 text-white text-xs rounded-md hover:bg-green-600"
                              >
                                View/Download
                              </a>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
                {/* NEW QUALIFICATIONS TAB CONTENT */}
                {activeTab === 'Qualifications' && (
                  <div>
                    <h3 className="text-xl font-semibold mb-4">Upload New Qualification</h3>

                    <div className="mb-6 p-4 border border-gray-200 rounded-md bg-gray-50">
                      <label htmlFor="qualificationUploadInput" className="block text-sm font-medium text-gray-700 mb-2">
                        Select Qualification File:
                      </label>
                      <input
                        type="file"
                        id="qualificationUploadInput"
                        onChange={handleQualificationFileChange}
                        className="block w-full text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-white focus:outline-none file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                      />
                      {selectedQualificationFile && (
                        <p className="mt-2 text-sm text-gray-600">Selected file: <span className="font-medium">{selectedQualificationFile.name}</span> ({formatFileSize(selectedQualificationFile.size)})</p>
                      )}
                      {can('employees', 'write') && (
                        <button
                          onClick={handleQualificationUpload}
                          className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                          disabled={qualificationsLoading || !selectedQualificationFile}
                        >
                          {qualificationsLoading ? 'Uploading...' : 'Upload Qualification'}
                        </button>
                      )}
                      {qualificationsError && <p className="text-red-500 text-sm mt-2">{qualificationsError}</p>}
                    </div>

                    <h4 className="text-lg font-semibold text-gray-800 mb-3">Existing Qualifications:</h4>
                    {qualificationsLoading && <p className="text-gray-500">Loading qualifications...</p>}
                    {employeeQualifications.length === 0 && !qualificationsLoading && !qualificationsError && (
                      <p className="text-gray-600">No qualifications found for this employee.</p>
                    )}
                    {employeeQualifications.length > 0 && (
                      <div className="space-y-4">
                        {employeeQualifications.map((qualification) => (
                          <div key={qualification.id} className="bg-gray-50 p-4 rounded-md shadow-sm border border-gray-200 flex justify-between items-center">
                            <div>
                              <p className="text-gray-800 text-sm font-medium">{qualification.file_name}</p>
                              <p className="text-gray-500 text-xs">
                                {formatFileSize(qualification.file_size)} &bull; {new Date(qualification.uploaded_at).toLocaleDateString()}
                              </p>
                            </div>
                            <a
                              href={`${API_URL}/uploads/${qualification.file_path}`} // Assuming qualifications are stored in the same 'uploads' directory
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-3 py-1 bg-green-500 text-white text-xs rounded-md hover:bg-green-600"
                            >
                              View/Download
                            </a>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            {!employee && !loading && !error && (
              <p className="text-center text-gray-500">No employee data available.</p>
            )}
          </div>
        </div>
      </div>
    {/* Edit Employee Modal - ADD THIS ENTIRE BLOCK */}
      {isEditModalOpen && can('employees', 'write') && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex justify-center items-center z-50">
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-xl font-bold text-gray-800">Edit Employee</h3>
              <button
                onClick={() => setIsEditModalOpen(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
              >
                ×
              </button>
            </div>
            
            <div className="p-6">
              {saveError && (
                <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                  {saveError}
                </div>
              )}
              
              <form onSubmit={(e) => { e.preventDefault(); handleSaveEdit(); }}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Full Name */}
                  <div className="md:col-span-2">
                    <label className="block text-gray-700 font-bold mb-2 text-sm">Full Name *</label>
                    <input
                      type="text"
                      value={editFormData.name || ''}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  {/* Surname - ADD THIS NEW FIELD RIGHT HERE */}
  <div className="md:col-span-2">
    <label className="block text-gray-700 font-bold mb-2 text-sm">Surname *</label>
    <input
      type="text"
      value={editFormData.surname || ''}
      onChange={(e) => setEditFormData(prev => ({ ...prev, surname: e.target.value }))}
      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
      required
    />
  </div>
                  
                  {/* ID/Passport Number */}
                  <div>
                    <label className="block text-gray-700 font-bold mb-2 text-sm">ID/Passport Number *</label>
                    <input
                      type="text"
                      value={editFormData.id_passport_number || ''}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, id_passport_number: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  
                  {/* Contact Number */}
                  <div>
                    <label className="block text-gray-700 font-bold mb-2 text-sm">Contact Number *</label>
                    <input
                      type="tel"
                      value={editFormData.contact_number || ''}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, contact_number: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  
                  {/* Email */}
                  <div>
                    <label className="block text-gray-700 font-bold mb-2 text-sm">Email *</label>
                    <input
                      type="email"
                      value={editFormData.email || ''}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, email: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  
                  {/* Employee Code */}
                  <div>
                    <label className="block text-gray-700 font-bold mb-2 text-sm">Employee Code</label>
                    <input
                      type="text"
                      value={editFormData.employee_code || ''}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, employee_code: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  
                  {/* Work Model */}
                  <div>
                    <label className="block text-gray-700 font-bold mb-2 text-sm">Work Model</label>
                    <select
                      value={editFormData.work_model || ''}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, work_model: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select Work Model</option>
                      <option value="Remote">Remote</option>
                      <option value="Hybrid">Hybrid</option>
                      <option value="On-site">On-site</option>
                    </select>
                  </div>
                  
                  {/* Position */}
                  <div>
                    <label className="block text-gray-700 font-bold mb-2 text-sm">Position</label>
                    <input
                      type="text"
                      value={editFormData.position || ''}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, position: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  
                  {/* Roles */}
                  <div>
                    <label className="block text-gray-700 font-bold mb-2 text-sm">Roles</label>
                    <input
                      type="text"
                      value={editFormData.roles || ''}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, roles: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  
                  {/* Employment Start Date */}
                  <div>
                    <label className="block text-gray-700 font-bold mb-2 text-sm">Employment Start Date</label>
                    <input
                      type="date"
                      value={editFormData.employment_start_date?.split('T')[0] || ''}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, employment_start_date: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  
                  {/* Status */}
                  <div>
                    <label className="block text-gray-700 font-bold mb-2 text-sm">Status</label>
                    <select
                      value={editFormData.active ? 'true' : 'false'}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, active: e.target.value === 'true' }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="true">Active</option>
                      <option value="false">Inactive</option>
                    </select>
                  </div>
                  
                  {/* RE Compliant */}
                  <div>
                    <label className="block text-gray-700 font-bold mb-2 text-sm">RE Compliant</label>
                    <select
                      value={editFormData.re_compliant ? 'true' : 'false'}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, re_compliant: e.target.value === 'true' }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="true">Compliant</option>
                      <option value="false">Non-Compliant</option>
                    </select>
                  </div>
                </div>
                
                {/* Modal Buttons */}
                <div className="flex justify-end gap-4 mt-6 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setIsEditModalOpen(false)}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSaving}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
                  >
                    {isSaving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeDetail;